import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import {
  AddLeafNodeRequest,
  ChartOfAccountsNodeViewModel,
  ChartOfAccountsTreeViewModel,
} from '../entities/chart-of-accounts.model';

/**
 * Cliente HTTP para o `ChartOfAccountsController` (treasury_service).
 * Contratos PascalCase 1:1 com o backend; nenhum mapper de casing.
 *
 * Source of truth: `libs/domains/treasury/CHART_OF_ACCOUNTS_API.md`
 */
@Injectable({ providedIn: 'root' })
export class ChartOfAccountsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.treasury_service;

  /**
   * `GET /ChartOfAccounts/GetTree`
   * Retorna a árvore completa do tenant (planta o seed default na primeira chamada).
   */
  public getTree(): Observable<ChartOfAccountsTreeViewModel> {
    return this.http.get<ChartOfAccountsTreeViewModel>(
      `${this.baseUrl}/ChartOfAccounts/GetTree`,
      { withCredentials: true },
    );
  }

  /**
   * `POST /ChartOfAccounts/AddLeafNode`
   * Cria uma conta-folha sob um nó-pai não-folha existente. Servidor gera
   * `Code` e herda `Nature`/`DreSection` do pai. Erros 400 vêm como texto plano.
   */
  public addLeafNode(
    request: AddLeafNodeRequest,
  ): Observable<ChartOfAccountsNodeViewModel> {
    return this.http.post<ChartOfAccountsNodeViewModel>(
      `${this.baseUrl}/ChartOfAccounts/AddLeafNode`,
      request,
      { withCredentials: true },
    );
  }

  /**
   * `POST /ChartOfAccounts/Activate/{nodeId}`
   * Reativa um nó previamente desativado.
   */
  public activate(nodeId: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/ChartOfAccounts/Activate/${encodeURIComponent(nodeId)}`,
      null,
      { withCredentials: true },
    );
  }

  /**
   * `POST /ChartOfAccounts/Deactivate/{nodeId}`
   * Marca o nó como inativo. **Não cascateia** para filhos. Lançamentos
   * vinculados permanecem intactos.
   */
  public deactivate(nodeId: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/ChartOfAccounts/Deactivate/${encodeURIComponent(nodeId)}`,
      null,
      { withCredentials: true },
    );
  }
}
